import { useCallback, useEffect, useRef, useState } from "react";
import type { LocationBatchResponse } from "@ehes/contracts";
import { AlertTriangle, LocateFixed, MapPin, RefreshCw, Square } from "lucide-react";
import { Button, Notice } from "../../components/ui";
import { api } from "../../lib/api";
import { shouldRecordWebLocation, type WebLocationSample } from "./trackingPolicy";
import {
  acknowledgeWebLocationBatch,
  deferWebLocationBatch,
  enqueueWebLocation,
  getPendingWebLocationBatch,
  getWebLocationQueueCount,
} from "./webLocationQueue";

type WebForegroundTrackingCardProps = {
  tripId: string;
  enabled: boolean;
  onQueueCountChange?: (count: number) => void;
  onTrackingChange?: (active: boolean) => void;
};

type TrackingState = "idle" | "starting" | "tracking" | "paused";

function isSecureTrackingContext() {
  return window.isSecureContext || ["localhost", "127.0.0.1"].includes(window.location.hostname);
}

export function WebForegroundTrackingCard({
  tripId,
  enabled,
  onQueueCountChange,
  onTrackingChange,
}: WebForegroundTrackingCardProps) {
  const [state, setState] = useState<TrackingState>("idle");
  const [queueCount, setQueueCount] = useState(0);
  const [lastLocationAt, setLastLocationAt] = useState<string | null>(null);
  const [lastUploadAt, setLastUploadAt] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const watchIdRef = useRef<number | null>(null);
  const userStartedRef = useRef(false);
  const uploadPromiseRef = useRef<Promise<void> | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const lastSampleRef = useRef<WebLocationSample | null>(null);
  const positionChainRef = useRef(Promise.resolve());
  const mountedRef = useRef(true);
  const onQueueCountChangeRef = useRef(onQueueCountChange);
  const onTrackingChangeRef = useRef(onTrackingChange);

  useEffect(() => {
    onQueueCountChangeRef.current = onQueueCountChange;
  }, [onQueueCountChange]);

  useEffect(() => {
    onTrackingChangeRef.current = onTrackingChange;
  }, [onTrackingChange]);

  const updateQueueCount = useCallback(async () => {
    const count = await getWebLocationQueueCount(tripId);
    if (!mountedRef.current) return count;
    setQueueCount(count);
    onQueueCountChangeRef.current?.(count);
    return count;
  }, [tripId]);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
  }, []);

  const flushQueue = useCallback((): Promise<void> => {
    if (uploadPromiseRef.current) return uploadPromiseRef.current;

    const upload = (async () => {
      clearRetryTimer();
      while (navigator.onLine && document.visibilityState === "visible") {
        const batch = await getPendingWebLocationBatch(tripId, 100);
        if (batch.length === 0) break;
        try {
          const result = await api<LocationBatchResponse>(`/drivers/trips/${encodeURIComponent(tripId)}/locations`, {
            method: "POST",
            body: JSON.stringify({
              events: batch.map(({ tripId: _tripId, attemptCount: _attemptCount, nextAttemptAt: _nextAttemptAt, ...event }) => {
                void _tripId;
                void _attemptCount;
                void _nextAttemptAt;
                return event;
              }),
            }),
          });
          if (result.acceptedCount + result.duplicateCount !== batch.length) {
            throw new Error("Sunucu konum paketini eksik onayladı.");
          }
          await acknowledgeWebLocationBatch(batch.map(event => event.eventId));
          if (mountedRef.current) {
            setLastUploadAt(new Date().toISOString());
            setError("");
          }
          await updateQueueCount();
        } catch (uploadError) {
          const retryDelay = await deferWebLocationBatch(batch);
          if (mountedRef.current) {
            setError(uploadError instanceof Error ? uploadError.message : "Konumlar gönderilemedi; tarayıcıda saklanıyor.");
          }
          if (mountedRef.current) {
            retryTimerRef.current = window.setTimeout(() => {
              retryTimerRef.current = null;
              if (document.visibilityState === "visible") void flushQueue();
            }, retryDelay);
          }
          break;
        }
      }
      const remainingCount = await updateQueueCount();
      if (mountedRef.current && remainingCount > 0 && retryTimerRef.current === null) {
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          if (document.visibilityState === "visible") void flushQueue();
        }, 30_000);
      }
    })().finally(() => {
      if (uploadPromiseRef.current === upload) uploadPromiseRef.current = null;
    });
    uploadPromiseRef.current = upload;
    return upload;
  }, [clearRetryTimer, tripId, updateQueueCount]);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = null;
    lastSampleRef.current = null;
  }, []);

  const startWatch = useCallback(() => {
    if (watchIdRef.current !== null || !userStartedRef.current || document.visibilityState !== "visible") return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      position => {
        positionChainRef.current = positionChainRef.current.then(async () => {
          const recordedAtMs = Number.isFinite(position.timestamp) ? position.timestamp : Date.now();
          const speed = position.coords.speed !== null && position.coords.speed >= 0
            ? position.coords.speed
            : null;
          const sample: WebLocationSample = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            recordedAtMs,
            speed,
          };
          if (!shouldRecordWebLocation(lastSampleRef.current, sample)) return;
          if (!Number.isFinite(position.coords.accuracy) || position.coords.accuracy < 0 || position.coords.accuracy > 5_000) {
            throw new Error("Konum doğruluğu gönderim sınırlarının dışında.");
          }
          await enqueueWebLocation(tripId, {
            recordedAt: new Date(recordedAtMs).toISOString(),
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            speed,
            heading: position.coords.heading !== null && position.coords.heading >= 0 && position.coords.heading < 360
              ? position.coords.heading
              : null,
            altitude: position.coords.altitude !== null && Number.isFinite(position.coords.altitude)
              ? position.coords.altitude
              : null,
          });
          lastSampleRef.current = sample;
          if (mountedRef.current) {
            setLastLocationAt(new Date(recordedAtMs).toISOString());
            setError("");
          }
          await updateQueueCount();
          void flushQueue();
        }).catch(locationError => {
          if (mountedRef.current) setError(locationError instanceof Error ? locationError.message : "Konum kaydedilemedi.");
        });
      },
      locationError => {
        if (!mountedRef.current) return;
        setError(locationError.code === locationError.PERMISSION_DENIED
          ? "Konum izni verilmedi. Tarayıcı ayarlarından bu siteye konum izni verin."
          : locationError.message || "Konum alınamadı.");
        if (locationError.code === locationError.PERMISSION_DENIED) {
          userStartedRef.current = false;
          onTrackingChangeRef.current?.(false);
          stopWatch();
          setState("idle");
        }
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 30_000 },
    );
    setState("tracking");
  }, [flushQueue, stopWatch, tripId, updateQueueCount]);

  useEffect(() => {
    mountedRef.current = true;
    void updateQueueCount().then(() => flushQueue()).catch(queueError => {
      if (mountedRef.current) setError(queueError instanceof Error ? queueError.message : "Konum kuyruğu açılamadı.");
    });

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        stopWatch();
        if (userStartedRef.current) setState("paused");
        return;
      }
      if (userStartedRef.current) startWatch();
      void flushQueue();
    };
    const handleOnline = () => {
      setIsOnline(true);
      if (document.visibilityState === "visible") void flushQueue();
    };
    const handleOffline = () => setIsOnline(false);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      mountedRef.current = false;
      userStartedRef.current = false;
      onTrackingChangeRef.current?.(false);
      stopWatch();
      clearRetryTimer();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [clearRetryTimer, flushQueue, startWatch, stopWatch, updateQueueCount]);

  const startTracking = () => {
    setError("");
    if (!enabled) {
      setError("Takip yalnızca devam eden ve aktif aracı bağlı bir seferde başlatılabilir.");
      return;
    }
    if (!isSecureTrackingContext()) {
      setError("Konum takibi yalnızca güvenli HTTPS bağlantısında kullanılabilir.");
      return;
    }
    if (!("geolocation" in navigator)) {
      setError("Bu tarayıcı konum takibini desteklemiyor.");
      return;
    }
    setState("starting");
    userStartedRef.current = true;
    onTrackingChangeRef.current?.(true);
    startWatch();
  };

  const stopTracking = () => {
    userStartedRef.current = false;
    onTrackingChangeRef.current?.(false);
    stopWatch();
    setState("idle");
    void flushQueue();
  };

  return (
    <section className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
      <div className="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <div className="flex items-center gap-2 font-bold text-navy"><LocateFixed size={18} className="text-teal" /> Web Konum Takibi</div>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Bu yedek takip yalnızca bu sayfa ekranda açıkken konum toplar. Ekran kilitliyken veya tarayıcı arka plandayken kesintisiz takip için EHES Şoför uygulamasını kullanın.
          </p>
        </div>
        {state === "tracking" || state === "paused" ? (
          <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={stopTracking}><Square size={15} /> Takibi Durdur</Button>
        ) : (
          <Button type="button" className="w-full sm:w-auto" disabled={!enabled} loading={state === "starting"} onClick={startTracking}><MapPin size={16} /> Takibi Başlat</Button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 divide-x overflow-hidden rounded-2xl bg-mist">
        <div className="p-3 text-center"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">GPS</p><p className="mt-1 text-xs font-semibold text-navy sm:text-sm">{state === "tracking" ? "Güçlü" : state === "paused" ? "Bekliyor" : "Kapalı"}</p></div>
        <div className="p-3 text-center"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Kuyruk</p><p className="mt-1 text-xs font-semibold text-navy sm:text-sm">{queueCount} konum</p></div>
        <div className="p-3 text-center"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Yükleme</p><p className="mt-1 truncate text-xs font-semibold text-navy sm:text-sm">{lastUploadAt ? new Date(lastUploadAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : lastLocationAt ? "Bekliyor" : "—"}</p></div>
      </div>

      {!isOnline && <Notice className="mt-4" tone="warning"><AlertTriangle size={16} /> İnternet bağlantısı yok. Konumlar tarayıcı kuyruğunda tutuluyor.</Notice>}
      {error && <Notice className="mt-4" tone="error">{error}</Notice>}
      {queueCount > 0 && <Button type="button" variant="ghost" className="mt-3" onClick={() => void flushQueue()}><RefreshCw size={15} /> Kuyruğu Şimdi Gönder</Button>}
    </section>
  );
}
